import { useCallback, useMemo } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

/**
 * The publishing sign-off process: an event moves through a chain of approvers
 * before carers and attendees can see it.
 *
 * Not to be confused with the carer approvals shown on the attendee roll. Those
 * are permission slips for a child to attend; this is colleagues signing an
 * event off for release. The API keeps them apart by calling this "publish",
 * and so does this file.
 *
 * Responses are PascalCase: FastAPI serialises its models by alias.
 */

const EVENT = "event/admin/";
const PUBLISH = "event/admin/publish/";

/**
 * The API's own wording for a refusal, when it is safe to show.
 *
 * These endpoints answer a 409 with a short, deliberate sentence: "Event is
 * already in review", "No one currently holds: hod". Those say precisely what
 * went wrong, and a caller inventing its own guess instead — "it may be this,
 * or it may be that" — leaves someone re-reading a screen that already knows
 * the answer.
 *
 * Only 4xx, only a string, and capped. A 5xx body can carry internals, and an
 * error body generally can quote the request back, which for an event payload
 * means attendee PII.
 */
export function refusalDetail(error: unknown): string | null {
	const res = (error as { response?: { status?: number; data?: unknown } })
		?.response;
	const status = res?.status ?? 0;
	if (status < 400 || status >= 500) return null;
	const detail = (res?.data as { detail?: unknown } | undefined)?.detail;
	return typeof detail === "string" && detail.trim()
		? detail.trim().slice(0, 200)
		: null;
}

/** Everything a workflow can be. There is no "draft": an event that was never
    submitted simply has no workflow at all. */
export type PublishStatus =
	"in_review" | "changes_requested" | "declined" | "published";

/** What an approver may do at their step. */
export type PublishDecision = "approved" | "declined" | "changes";

export interface PublishActor {
	_id?: string | number;
	Name?: string;
}

export interface PublishStep {
	Order: number;
	RoleId: string;
	Name?: string;
	Decision?: string | null;
	DecidedAt?: string | null;
	DecidedBy?: PublishActor | null;
	Reason?: string | null;
	/** Set only on a derived step (the submitter's direct manager): the person
	    worked out at submit and fixed for the life of the review. */
	ApproverId?: string | null;
	ApproverName?: string | null;
}

export interface PublishWorkflow {
	EventId: string;
	WorkflowCode?: string | null;
	WorkflowName?: string | null;
	Status?: PublishStatus | null;
	CurrentStep?: number | null;
	CurrentRoleId?: string | null;
	CurrentApproverId?: string | null;
	SubmittedAt?: string | null;
	SubmittedBy?: PublishActor | null;
	Steps: PublishStep[];
	History: {
		At?: string;
		By?: PublishActor;
		Action?: string;
		Step?: number;
		RoleId?: string;
		Decision?: string;
		Reason?: string;
	}[];
	/** Whether the signed-in user may decide the CURRENT step. Server-decided,
	    so the buttons never have to work out role membership themselves. */
	CanDecide: boolean;
}

export interface PublishQueueRow {
	EventId: string;
	Title?: string;
	StartDateTime?: string;
	EndDateTime?: string;
	Status?: PublishStatus;
	CurrentStep?: number;
	CurrentRoleId?: string;
	CurrentRoleName?: string;
	SubmittedAt?: string;
	SubmittedBy?: PublishActor;
}

export interface PublishRoleHolder {
	_id: string;
	RoleId: string;
	ContactId: string;
	Name?: string;
	/** Their job — `StaffInfo.Title` on the contact. Optional: the API does not
	    send it yet, and a row without one simply shows the name. */
	Title?: string;
	ValidFrom?: string | null;
	ValidTo?: string | null;
	DelegateTo?: string | null;
	DelegateName?: string | null;
	/** The server's own verdict on whether the tenure is live, so the client
	    does not re-implement the date comparison and disagree with it. */
	Live: boolean;
}

export interface PublishRole {
	Code: string;
	Name?: string;
	Description?: string;
	Holders: PublishRoleHolder[];
	/** Worked out per event rather than held, so there is nobody to appoint and
	    `Holders` is always empty. */
	Derived?: boolean;
}

/**
 * The ten values `Event.EventType` actually holds.
 *
 * A constant rather than a dictionary read: the `EventTypes` dictionary is a
 * different vocabulary — Class, Assessments, Cover, Yard Duty — belonging to the
 * timetable, and nothing else lists these. Taken from the collection itself.
 * Move it to a dictionary the day the school wants to add one without a release.
 */
/** One step of a chain TEMPLATE — no decision, because nobody has decided yet. */
export interface PublishChainStep {
	Order: number;
	RoleId: string;
	Name?: string;
}

/** A chain as the dictionary holds it. */
export interface PublishChainTemplate {
	Code: string;
	Name?: string;
	/**
	 * Two independent dimensions plus the fallback, matched in this order:
	 * `EventTypes` (what the event IS, from the EventActivityTypes catalogue),
	 * then `Calendars` (where it came FROM), then `Default`. A workflow naming
	 * none of the three governs nothing, which is legal and is how one is parked
	 * without being deleted.
	 */
	AppliesTo: {
		EventTypes?: string[];
		Calendars?: string[];
		Default?: boolean;
	};
	Steps: PublishChainStep[];
}

/**
 * The chain templates: which roles sign off which kinds of event.
 *
 * The chain an event runs is SNAPSHOTTED onto it at submit, so editing a
 * template never rewrites a chain that has already been decided on — it decides
 * what the next submission gets. Events currently in review on an edited chain
 * are dropped back to `changes_requested` by the API, the same way a material
 * edit to an event invalidates its sign-off; the screen says so before saving.
 */
export function usePublishChains(enabled = true) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ["publish", "workflows"],
		queryFn: async (): Promise<PublishChainTemplate[]> => {
			const res = await callPymbleApi(instance, "GET", `${PUBLISH}workflows/`);
			return Array.isArray(res) ? res : [];
		},
		enabled: isAuthenticated && enabled,
	});

	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: ["publish", "workflows"] });

	/* Whole-chain writes rather than per-step ones. A chain is an ORDER, and
	   patching one step at a time would make the order a sequence of separate
	   writes that can interleave — two admins reordering at once would produce a
	   chain neither of them chose. */
	const saveChain = useMutation({
		mutationFn: async (chain: PublishChainTemplate) =>
			callPymbleApi(instance, "PUT", `${PUBLISH}workflows/${chain.Code}`, {
				data: chain,
			}),
		onSuccess: refresh,
	});

	const createChain = useMutation({
		mutationFn: async (chain: PublishChainTemplate) =>
			callPymbleApi(instance, "POST", `${PUBLISH}workflows/`, { data: chain }),
		onSuccess: refresh,
	});

	const deleteChain = useMutation({
		mutationFn: async (code: string) =>
			callPymbleApi(instance, "DELETE", `${PUBLISH}workflows/${code}`),
		onSuccess: refresh,
	});

	return {
		chains: useMemo(() => query.data ?? [], [query.data]),
		chainsLoading: query.isLoading,
		chainsError: !!query.error,
		saveChain: saveChain.mutateAsync,
		createChain: createChain.mutateAsync,
		deleteChain: deleteChain.mutateAsync,
		chainsSaving:
			saveChain.isPending || createChain.isPending || deleteChain.isPending,
	};
}

/**
 * What a workflow's status is CALLED on screen. One label per status, and
 * nothing but the status decides it.
 *
 * `changes_requested` does cover two different events: an approver asking for
 * changes, and the organiser editing something material after sign-off, which
 * drops the workflow by design. This used to split them, labelling the second
 * "Reset by edit" off the `invalidated` entry in History. That is gone — the
 * distinction is still available where it belongs, in the "What changed" note
 * beside the pill, which says what happened rather than renaming the state it
 * left the event in.
 */
export function publishStatusLabel(status: string | null | undefined): string {
	switch (status) {
		case "published":
			return "Published";
		case "declined":
			return "Declined";
		case "changes_requested":
			return "Changes requested";
		case "in_review":
			return "Pending";
		default:
			return "Not submitted";
	}
}

/** Why a chain stopped, for the screens that have to explain it. */
export interface PublishFeedback {
	/** `changes` and `declined` come from an approver; `invalidated` is the
	    event's own editor having changed something material. */
	kind: "changes" | "declined" | "invalidated";
	reason?: string;
	by?: string;
	at?: string;
}

/**
 * The reason this event is not moving, taken from the workflow's History.
 *
 * NOT from `Steps`, which is where both screens looked before and why a stopped
 * event could sit there explaining nothing. A re-submit REPLACES `Steps` — the
 * decisions were given against a different version of the event — so the note an
 * approver wrote survives only in `History`. An invalidation writes no step at
 * all: the chain is dropped because the organiser changed something material,
 * and the only record is the history entry.
 *
 * Read backwards, so the answer is the most recent thing that stopped it.
 */
export function publishFeedback(
	workflow: PublishWorkflow | null,
): PublishFeedback | null {
	if (!workflow) return null;
	const status = workflow.Status;
	if (status !== "changes_requested" && status !== "declined") return null;

	for (let i = workflow.History.length - 1; i >= 0; i -= 1) {
		const entry = workflow.History[i];
		/* A later submit means everything before it has been answered — the
		   event is stopped for a newer reason, or the history is simply not
		   telling us why. Better to say nothing than to quote a note about a
		   version of the event that no longer exists. */
		if (entry.Action === "submitted" || entry.Action === "resubmitted") {
			return null;
		}
		if (entry.Action === "invalidated") {
			return {
				kind: "invalidated",
				reason: entry.Reason,
				by: entry.By?.Name,
				at: entry.At,
			};
		}
		if (
			entry.Action === "decision" &&
			(entry.Decision === "changes" || entry.Decision === "declined")
		) {
			return {
				kind: entry.Decision === "declined" ? "declined" : "changes",
				reason: entry.Reason,
				by: entry.By?.Name,
				at: entry.At,
			};
		}
	}
	return null;
}

/**
 * The caller's event permission flags, on their own.
 *
 * `event/admin/` answers `{permissions, events}`, so one row is enough to learn
 * what someone may do. A dedicated query rather than a borrowed one: the
 * sign-off and role screens need the flags before they know what to show, and
 * reading them off a list they may not even fetch is what previously told an
 * administrator they had no permission at all.
 *
 * One row, `manual` only — the cheapest shape of that response. Cached under its
 * own key, so both screens share the single request.
 */
export function useEventPermissions() {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const query = useQuery({
		queryKey: ["event", "permissions"],
		queryFn: async () =>
			callPymbleApi(instance, "GET", `${EVENT}?limit=1&categories=manual`),
		enabled: isAuthenticated,
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const data = query.data as any;
	return {
		permissions: (data?.permissions ?? undefined) as
			Record<string, boolean> | undefined,
		permissionsLoading: query.isLoading,
	};
}

/**
 * One event's workflow.
 *
 * `enabled` lets a caller hold off until a row is actually selected, which is
 * how the sign-off panel avoids fetching for every row the pointer crosses.
 */
export function usePublishWorkflow(eventId: string | null) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ["publish", "workflow", eventId],
		queryFn: async (): Promise<PublishWorkflow | null> =>
			callPymbleApi(instance, "GET", `${EVENT}${eventId}/publish/`),
		enabled: isAuthenticated && !!eventId,
	});

	/* Both writes return the new workflow, so the cache is set from the reply.
	   The lists that count by status have to go, and so does the event itself:
	   publishing changes what carers can see.

	   The workflow query is invalidated as WELL as being set, because the reply
	   is not guaranteed to arrive: `callPymbleApi` answers null rather than
	   throwing when a token has to be renewed mid-flight. Setting from the reply
	   alone left the write done on the server and the tab still holding its
	   pre-submit copy — a success toast, and the Submit button still sitting
	   there, until something else forced a refetch. */
	const settle = useCallback(
		(next: PublishWorkflow | null) => {
			if (next)
				queryClient.setQueryData(["publish", "workflow", eventId], next);
			queryClient.invalidateQueries({
				queryKey: ["publish", "workflow", eventId],
			});
			queryClient.invalidateQueries({ queryKey: ["publish", "list"] });
			queryClient.invalidateQueries({ queryKey: ["publish", "queue"] });
			queryClient.invalidateQueries({ queryKey: ["event", "v2", eventId] });
			/* `["events"]`, not `["events", "list"]` — that prefix reaches only the
			   calendar, and the Cards, List and search views are separate caches
			   beneath it. A sign-off decision changes what the rail's publish
			   facets and the row's chips should say, so all three go. See the
			   note on the update mutation in useEventManager. */
			queryClient.invalidateQueries({ queryKey: ["events"] });
		},
		[queryClient, eventId],
	);

	const submitMutation = useMutation({
		mutationFn: async (): Promise<PublishWorkflow | null> =>
			callPymbleApi(instance, "POST", `${EVENT}${eventId}/publish/submit/`),
		onSuccess: settle,
	});

	/* Withdrawal is not a decision, so it is its own mutation: it reaches past
	   the chain rather than moving through it, and the API gates it on
	   `event_publisher` or `event_admin` rather than on holding a step's role.
	   The reply is the workflow after the fact, so `settle` handles it exactly
	   as a decision's — the event goes back to `changes_requested`, and the
	   lists that count by status have to be refetched either way. */
	const withdrawMutation = useMutation({
		mutationFn: async (reason: string): Promise<PublishWorkflow | null> =>
			callPymbleApi(instance, "POST", `${EVENT}${eventId}/publish/withdraw/`, {
				data: { Reason: reason },
			}),
		onSuccess: settle,
	});

	const decideMutation = useMutation({
		mutationFn: async (vars: {
			decision: PublishDecision;
			reason?: string;
		}): Promise<PublishWorkflow | null> =>
			callPymbleApi(instance, "POST", `${EVENT}${eventId}/publish/decision/`, {
				data: { Decision: vars.decision, Reason: vars.reason ?? null },
			}),
		onSuccess: settle,
	});

	return {
		workflow: query.data ?? null,
		workflowLoading: query.isLoading,
		workflowError: !!query.error,
		/* Distinct from `workflowLoading`, which is only the FIRST load. A
		   refetch after a submit keeps the old data on screen, and the Submit
		   button has to be inert during it or it fires the write twice. */
		workflowFetching: query.isFetching,
		submit: submitMutation.mutateAsync,
		submitting: submitMutation.isPending,
		decide: decideMutation.mutateAsync,
		deciding: decideMutation.isPending,
		withdraw: withdrawMutation.mutateAsync,
		withdrawing: withdrawMutation.isPending,
	};
}

/**
 * The queue: events whose CURRENT step names a role the signed-in user holds.
 *
 * Paged by the server. The endpoint is rate limited to 60 requests a minute, so
 * nothing here polls — it refetches when a decision settles and not otherwise.
 */
export function usePublishQueue(page: number, limit: number, enabled = true) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const query = useQuery({
		queryKey: ["publish", "queue", page, limit],
		queryFn: async () =>
			callPymbleApi(
				instance,
				"GET",
				`${PUBLISH}queue/?page=${page}&limit=${limit}`,
			),
		enabled: isAuthenticated && enabled,
	});

	const data = query.data as
		| { Total: number; Page: number; Limit: number; Rows: PublishQueueRow[] }
		| undefined;

	return {
		rows: useMemo(() => data?.Rows ?? [], [data]),
		total: data?.Total ?? 0,
		queueLoading: query.isLoading,
		queueError: !!query.error,
	};
}

/** The role catalogue with its holders. Rate limited to 30 a minute. */
export function usePublishRoles(enabled = true) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ["publish", "roles"],
		queryFn: async (): Promise<PublishRole[]> => {
			const res = await callPymbleApi(instance, "GET", `${PUBLISH}roles/`);
			return Array.isArray(res) ? res : [];
		},
		enabled: isAuthenticated && enabled,
	});

	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: ["publish", "roles"] });

	const addHolder = useMutation({
		mutationFn: async (vars: { roleId: string; contactId: string }) =>
			callPymbleApi(
				instance,
				"POST",
				`${PUBLISH}roles/${vars.roleId}/holders/`,
				{ data: { ContactId: vars.contactId } },
			),
		onSuccess: refresh,
	});

	/* DELETE ends a tenure by setting ValidTo rather than removing the row, so a
	   decision taken last term still names whoever held the role then. */
	const endHolder = useMutation({
		mutationFn: async (vars: { roleId: string; holderId: string }) =>
			callPymbleApi(
				instance,
				"DELETE",
				`${PUBLISH}roles/${vars.roleId}/holders/${vars.holderId}`,
			),
		onSuccess: refresh,
	});

	const setDelegate = useMutation({
		mutationFn: async (vars: {
			roleId: string;
			holderId: string;
			delegateTo: string | null;
		}) =>
			callPymbleApi(
				instance,
				"PATCH",
				/* No trailing slash on this one, unlike its siblings: that is
				   how the route is declared, and a redirect on a PATCH can
				   drop the body. */
				`${PUBLISH}roles/${vars.roleId}/holders/${vars.holderId}/delegate`,
				{ data: { DelegateTo: vars.delegateTo } },
			),
		onSuccess: refresh,
	});

	return {
		roles: useMemo(() => query.data ?? [], [query.data]),
		rolesLoading: query.isLoading,
		rolesError: !!query.error,
		addHolder: addHolder.mutateAsync,
		endHolder: endHolder.mutateAsync,
		setDelegate: setDelegate.mutateAsync,
		rolesSaving:
			addHolder.isPending || endHolder.isPending || setDelegate.isPending,
	};
}

/**
 * Events at a given point in the process, from the admin list.
 *
 * `publish_status` accepts a status, `none` for events never submitted, or
 * `any` for everything that has a workflow. An unknown value is a 422 rather
 * than a silently unfiltered list.
 *
 * `all` is this hook's own word for omitting the parameter, which is not the
 * same as `any`: that means "has a workflow" and would hide every event nobody
 * has submitted. `unapproved` is everything except published, the never
 * submitted included.
 */
export function usePublishList(
	status: PublishStatus | "none" | "any" | "all" | "unapproved",
	limit: number,
	page: number,
	enabled = true,
	fromDate?: string,
	search?: string,
	categories?: string,
) {
	const { instance } = useMsal();
	const isAuthenticated = useIsAuthenticated();

	const query = useQuery({
		queryKey: [
			"publish",
			"list",
			status,
			limit,
			page,
			fromDate ?? "",
			search ?? "",
			categories ?? "",
		],
		queryFn: async () =>
			callPymbleApi(
				instance,
				"GET",
				`${EVENT}?limit=${limit}&page=${page}` +
					(status === "all" ? "" : `&publish_status=${status}`) +
					/* The queue endpoint takes no date filter, so "waiting on me"
					   shows everything assigned to you; the status tabs can be
					   bounded, and a sign-off list offering last term's events is
					   asking a question with no answer. */
					(fromDate ? `&from_date=${encodeURIComponent(fromDate)}` : "") +
					/* Searched on the SERVER. Filtering the fetched page instead
					   only ever searched the 15 rows already on screen, so an
					   event further down the list could not be found at all. */
					(search ? `&search=${encodeURIComponent(search)}` : "") +
					/* The admin list defaults to `edumate,manual`, which on an
					   unfiltered tab is 1,968 timetable events between now and
					   next year. A caller that only wants events made here says
					   so. */
					(categories ? `&categories=${encodeURIComponent(categories)}` : ""),
			),
		enabled: isAuthenticated && enabled,
	});

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const data = query.data as any;

	return {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		events: useMemo<any[]>(() => data?.events ?? [], [data]),
		/* `event/admin/` answers `{permissions, events}`, and this hook used to
		   keep only the events. The sign-off screen then asked a SECOND source
		   for the flags — the calendar's list query — which returns none unless
		   a category filter is passed, so an event administrator was told they
		   could not sign off events. The flags are right here, in a response
		   the screen already makes on every tab. */
		permissions: data?.permissions as Record<string, boolean> | undefined,
		listLoading: query.isLoading,
		listError: !!query.error,
	};
}
